// apps/web/src/components/coletor/tablet-operador-modal.tsx — Fase 17.9, Plano 02 (KAN-06, KAN-07, KAN-08, D-03..D-08).
import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { TabletLoginResponse, TabletOperadorResumo } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { cabecalhosDispositivo, type CredencialDispositivo } from '../../lib/kanban/dispositivo';
import {
  aplicarTeclaPin,
  autoresPendentesInelegiveis,
  classificarOperadores,
  formatarPontosPin,
} from '../../lib/kanban/operador-tablet';
import { autoresPendentes } from '../../lib/offline/outbox';
import { TecladoNumerico } from './teclado-numerico';
import { X, ArrowLeft, AlertTriangle } from 'lucide-react';

export interface TabletOperadorModalProps {
  aberto: boolean;
  credencial: CredencialDispositivo | null;
  acaoPendenteDescricao?: string | null;
  onSincronizar?: () => Promise<void>;
  onFechar: () => void;
  onAutenticado: (usuario: TabletLoginResponse['user']) => void;
}

export function TabletOperadorModal({
  aberto,
  credencial,
  acaoPendenteDescricao,
  onSincronizar,
  onFechar,
  onAutenticado,
}: TabletOperadorModalProps): ReactElement | null {
  const [passo, setPasso] = useState<'selecionar_operador' | 'digitar_pin'>('selecionar_operador');
  const [operadores, setOperadores] = useState<readonly TabletOperadorResumo[]>([]);
  const [autoresComPendencias, setAutoresComPendencias] = useState<readonly string[]>([]);
  const [operadorSelecionado, setOperadorSelecionado] = useState<TabletOperadorResumo | null>(null);
  const [pin, setPin] = useState<string>('');
  const [carregando, setCarregando] = useState<boolean>(false);
  const [enviando, setEnviando] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);
  // Callback mais recente em ref: uma arrow inline do pai não pode reiniciar o fluxo do modal (CR-01).
  const onSincronizarRef = useRef(onSincronizar);
  useEffect(() => {
    onSincronizarRef.current = onSincronizar;
  }, [onSincronizar]);

  useEffect(() => {
    if (!aberto) return;

    let ativo = true;
    setPasso('selecionar_operador');
    setOperadorSelecionado(null);
    setPin('');
    setErro(null);
    setCarregando(true);

    void (async () => {
      const sincronizar = onSincronizarRef.current;
      if (sincronizar) {
        await sincronizar().catch(() => {});
      }
      const pendentes = await autoresPendentes(credencial?.deviceId).catch(() => []);
      if (!ativo) return;
      setAutoresComPendencias(pendentes);

      try {
        const res = await fetch('/api/v1/auth/tablet/operadores', {
          headers: cabecalhosDispositivo(credencial),
        });
        if (res.ok) {
          const lista = (await res.json()) as TabletOperadorResumo[];
          if (ativo) setOperadores(lista);
        } else {
          if (ativo) setErro('Não foi possível carregar os operadores deste tablet.');
        }
      } catch {
        if (ativo) setErro('Falha de rede ao carregar operadores.');
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [aberto, credencial]);

  if (!aberto) return null;

  async function submeterPin(pinCompleto: string, operador: TabletOperadorResumo): Promise<void> {
    setEnviando(true);
    setErro(null);
    try {
      const res = await apiMutate('/api/v1/auth/tablet/login', {
        method: 'POST',
        headers: cabecalhosDispositivo(credencial),
        body: {
          userId: operador.id,
          pin: pinCompleto,
        },
      });

      if (res.status === 200) {
        const dados = (await res.json()) as TabletLoginResponse;
        onAutenticado(dados.user);
        onFechar();
        return;
      }

      if (res.status === 401) {
        setErro('PIN incorreto ou bloqueado temporariamente. Verifique e tente novamente.');
        setPin('');
        return;
      }

      if (res.status === 429) {
        setErro('Muitas tentativas. Aguarde um minuto.');
        setPin('');
        return;
      }

      setErro('Falha ao autenticar. Tente novamente.');
      setPin('');
    } catch {
      setErro('Falha de rede ao autenticar.');
      setPin('');
    } finally {
      setEnviando(false);
    }
  }

  function aoTeclar(tecla: string): void {
    if (enviando || !operadorSelecionado) return;
    const novoPin = aplicarTeclaPin(pin, tecla);
    setPin(novoPin);
    setErro(null);

    if (novoPin.length === 4) {
      void submeterPin(novoPin, operadorSelecionado);
    }
  }

  const inelegiveis = autoresPendentesInelegiveis(operadores, autoresComPendencias);
  const autoresBloqueantes = autoresComPendencias.filter((id) => !inelegiveis.includes(id));
  const nomesPendentes = autoresBloqueantes
    .map((id) => operadores.find((op) => op.id === id)?.displayName)
    .filter(Boolean)
    .join(', ');
  // Só afirma "sem acesso" com a lista de operadores carregada (falha de rede não é inelegibilidade).
  const mostrarAvisoInelegiveis = operadores.length > 0 && inelegiveis.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Identificação do operador"
      data-theme="dark-premium"
      data-density="touch"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl text-slate-100">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-2">
            {passo === 'digitar_pin' && (
              <button
                type="button"
                onClick={() => {
                  setPasso('selecionar_operador');
                  setPin('');
                  setErro(null);
                }}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Voltar para seleção de operador"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-lg font-bold text-white">
              {passo === 'selecionar_operador' ? 'Quem é você?' : 'Digite seu PIN'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onFechar}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Fechar modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {acaoPendenteDescricao && (
          <div className="mt-3 rounded-xl border border-emerald-900/60 bg-emerald-950/40 px-3 py-2 text-xs font-semibold text-emerald-300">
            Depois de entrar: {acaoPendenteDescricao}
          </div>
        )}

        {erro && (
          <div className="mt-3 rounded-xl border border-rose-900/60 bg-rose-950/40 px-3 py-2 text-xs font-semibold text-rose-300">
            {erro}
          </div>
        )}

        <div className="mt-4 flex-1 overflow-y-auto">
          {carregando ? (
            <p className="py-8 text-center text-sm text-slate-400">Carregando operadores...</p>
          ) : passo === 'selecionar_operador' ? (
            <div className="space-y-3">
              {autoresBloqueantes.length > 0 && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-800 bg-amber-950/50 p-3 text-xs text-amber-200">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
                  <p>
                    Há apontamentos de <span className="font-bold">{nomesPendentes || 'outro operador'}</span> ainda não
                    enviados. Entre como {nomesPendentes || 'ele'} para enviá-los antes de trocar de operador.
                  </p>
                </div>
              )}
              {mostrarAvisoInelegiveis && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-xl border border-rose-800 bg-rose-950/50 p-3 text-xs text-rose-200"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-rose-400" />
                  <p>
                    Há apontamentos salvos neste tablet por {inelegiveis.length === 1 ? 'um operador' : 'operadores'} que
                    não pode(m) mais entrar (desativado, sem PIN ou perfil alterado). Eles não podem ser enviados por
                    outra conta: <span className="font-bold">chame um supervisor</span> para revisá-los.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {classificarOperadores(operadores, autoresComPendencias).map(({ operador: op, desabilitado, motivo }) => (
                  <button
                    key={op.id}
                    type="button"
                    disabled={desabilitado}
                    onClick={() => {
                      setOperadorSelecionado(op);
                      setPasso('digitar_pin');
                      setPin('');
                      setErro(null);
                    }}
                    className="flex min-h-[48px] flex-col justify-center rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-left touch-manipulation hover:border-slate-500 hover:bg-slate-750 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-40 disabled:hover:border-slate-700 disabled:hover:bg-slate-800"
                  >
                    <span className="text-sm font-bold text-white">{op.displayName || op.username}</span>
                    {motivo === 'sem_pin' && <span className="text-[11px] text-slate-400">Sem PIN cadastrado</span>}
                    {motivo === 'aguardando_envio' && <span className="text-[11px] text-amber-400">Aguardando envio</span>}
                  </button>
                ))}
              </div>

              {operadores.length === 0 && (
                <p className="py-8 text-center text-sm text-slate-400">Nenhum operador elegível encontrado.</p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="mb-4 text-center">
                <p className="text-xs uppercase tracking-wider text-slate-400">Operador selecionado</p>
                <p className="text-base font-bold text-white">
                  {operadorSelecionado?.displayName || operadorSelecionado?.username}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setPasso('selecionar_operador');
                    setPin('');
                    setErro(null);
                  }}
                  className="mt-1 text-xs text-emerald-400 hover:underline"
                >
                  Trocar pessoa
                </button>
              </div>

              <div
                aria-label={`PIN: ${pin.length} de 4 dígitos`}
                className="mb-6 flex h-12 items-center justify-center rounded-xl border border-slate-700 bg-slate-950 px-6 font-mono text-2xl tracking-widest text-emerald-400"
              >
                {formatarPontosPin(pin)}
              </div>

              <div className="w-full max-w-xs">
                <TecladoNumerico onTecla={aoTeclar} desabilitado={enviando} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
